import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../utils/checkoutCalculations';
import toast from '../../shared/Toast';

/**
 * PaymentLinkScreen
 * Shows QR code, copy link, email link, and confirm payment.
 * Used for Payment Link, Klarna, and Clearpay flows.
 * Renders inline in the checkout panel (not a Bootstrap modal).
 */
const PaymentLinkScreen = ({ paymentLinkData, paymentType, onConfirm, onClose, onCheckStatus, currencySymbol, clientEmail, clientName }) => {
  const [copied, setCopied] = useState(false);
  const [checking, setChecking] = useState(false);
  const [email, setEmail] = useState(clientEmail || '');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const fmt = (a) => formatCurrency(a, currencySymbol);

  const checkoutUrl = paymentLinkData?.checkoutUrl || '';
  const amount = paymentLinkData?.amount || 0;

  // Auto-poll for payment status every 5 seconds
  useEffect(() => {
    if (!checkoutUrl || !onCheckStatus) return;
    const poll = setInterval(async () => {
      try {
        const result = await onCheckStatus();
        if (result && result.isPaymentLinkPaid === 1) {
          clearInterval(poll);
          toast.success('Payment received');
          onConfirm(result);
        }
      } catch { /* silent */ }
    }, 5000);
    return () => clearInterval(poll);
  }, [checkoutUrl, onCheckStatus, onConfirm]);

  // QR code via free API
  const qrUrl = checkoutUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(checkoutUrl)}`
    : '';

  // Payment type config
  const config = {
    payment: { title: 'Payment Link', subtitle: 'Share with client to pay', color: '#007EA0' },
    klarna: { title: 'Klarna', subtitle: 'Pay in 3 interest-free instalments', color: '#FFB3C7' },
    clearpay: { title: 'Clearpay', subtitle: 'Pay in 4 interest-free instalments', color: '#B2FCE4' },
  }[paymentType] || config?.payment;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(checkoutUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleSendEmail = async () => {
    if (!email.trim()) return;
    setSendingEmail(true);
    try {
      const { paymentService } = await import('../../../_services/payment.service');
      await paymentService.sendPaymentLinkEmail({
        email: email.trim(),
        paymentLink: checkoutUrl,
        amount,
        currency: currencySymbol,
        bookingId: paymentLinkData?.bookingId,
        clientName: clientName || '',
      });
      setEmailSent(true);
      toast.success('Payment link sent');
    } catch {
      toast.error('Failed to send email');
    }
    setSendingEmail(false);
  };

  const handleConfirmPayment = async () => {
    setChecking(true);
    try {
      const result = await onCheckStatus();
      if (result && result.isPaymentLinkPaid === 1) {
        toast.success('Payment confirmed');
        onConfirm(result);
      } else {
        toast.warning('Payment not received yet. Please wait for client to complete.');
      }
    } catch {
      toast.error('Could not verify payment. Please try again.');
    }
    setChecking(false);
  };

  return (
    <div className="co-pl">
      {/* Header */}
      <div className="co-screen-header">
        <button className="co-back-btn" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 className="co-screen-title">{config.title}</h2>
      </div>

      {/* Amount badge */}
      <div className="co-pl-amount">
        <span className="co-pl-amount-value">{fmt(amount)}</span>
        <span className="co-pl-amount-label">{config.subtitle}</span>
      </div>

      {/* QR Code */}
      {qrUrl && (
        <div className="co-pl-qr">
          <img src={qrUrl} alt="Scan to pay" className="co-pl-qr-img" />
          <p className="co-pl-qr-hint">Scan to pay</p>
        </div>
      )}

      {/* Link + Copy */}
      <div className="co-pl-link-row">
        <input
          className="co-pl-link-input"
          value={checkoutUrl}
          readOnly
          onClick={e => e.target.select()}
        />
        <button className={`co-pl-copy-btn ${copied ? 'co-pl-copy-btn--copied' : ''}`} onClick={handleCopy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {/* Email section */}
      <div className="co-pl-email-section">
        <div className="co-section-title">Send via email</div>
        <div className="co-pl-email-row">
          <input
            className="co-pl-email-input"
            type="email"
            placeholder="Client email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={emailSent}
          />
          <button
            className="co-pl-email-btn"
            onClick={handleSendEmail}
            disabled={!email.trim() || sendingEmail || emailSent}
          >
            {emailSent ? 'Sent' : sendingEmail ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="co-pl-footer">
        <button className="bdp-btn bdp-btn--secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          className="bdp-btn bdp-btn--primary"
          onClick={handleConfirmPayment}
          disabled={checking}
        >
          {checking ? 'Checking...' : 'Confirm Payment'}
        </button>
      </div>
    </div>
  );
};

export default PaymentLinkScreen;
